import React, { useMemo, useState } from 'react'
import { message, TimePicker } from 'antd'
import { SaveOutlined } from '@ant-design/icons'
import {
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import useAxios, { axiosInstance } from '../../../../../hooks/useAxios'
import { FacebookFilterMenuEnum } from './FacebookReplyChat'
import SelectSearch, { IOptionSelect } from '../../../../../components/SelectSearch'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import ImgTag from '../../../../../components/ImgTag'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../constants/errors'

interface IFacebookPage {
  _id: string;
  pageId: string;
  accessToken: string;
  createdAt: string;
  imageUrl: string;
  merchant: string;
  objectId: string;
  pageName: string;
  updatedAt: string;
}

interface IBotForm {
  page: string;
  message: string;
  status: FacebookFilterMenuEnum;
  times: Array<{
    time: string;
  }>
  images: string[];
}

const getBotFormValidationSchema = () => {
  return Yup.object({
    page: Yup.string().required('กรุณาเลือกเพจ'),
    message: Yup.string().required('กรุณาใส่ข้อความ'),
    status: Yup.string().oneOf(Object.values(FacebookFilterMenuEnum),
      'Invalid status',
    ).required('กรุณาเลือกสถานะ'),
    times: Yup.array().of(Yup.object({
      time: Yup.string().required('กรุณาเลือกเวลา'),
    })).max(4, 'สามารถเลือกได้สูงสุด 4 ครั้ง'),
    images: Yup.array().of(Yup.string()).max(10, 'สามารถเลือกได้สูงสุด 10 รูปภาพ'),
  })
}

const FacebookDiggerChat = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [searchPage, setSearchPage] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const onHandleSelectMultiFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      message.error('กรุณาเลือกไฟล์')
      return
    }

    const files = event.target.files
    const imageFiles = []

    for (let i = 0; i < files.length; i++) {
      const formData = new FormData()
      formData.append('file', files[i])
      try {
        const response = await axiosInstance.post('/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        imageFiles.push(response.data.url)
      } catch (e: any) {
        const messageError = ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] || 'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
    }
    formik.setFieldValue('images', [...formik.values.images, ...imageFiles.slice(0, 10 - formik.values.images.length)])
  }

  const pageList = useAxios<IFacebookPage[]>({
    method: 'GET',
    url: '/facebook/pages/available',
  })

  const menuItemsTypeStatus = useMemo(() => [
    { label: 'สำเร็จ ', value: FacebookFilterMenuEnum.Purchased },
    { label: 'รอดำเนินการ', value: FacebookFilterMenuEnum.Pendding },
    { label: 'ยังไม่สำเร็จ', value: FacebookFilterMenuEnum.Hold },
    { label: 'ยกเลิก', value: FacebookFilterMenuEnum.Cancelled },
  ], [])

  const formik = useFormik<IBotForm>({
    initialValues: {
      page: '',
      message: '',
      status: FacebookFilterMenuEnum.Purchased,
      times: [{ time: '' }],
      images: [],
    } as IBotForm,
    validationSchema: getBotFormValidationSchema(),
    onSubmit: async (values: IBotForm) => {
      setIsLoading(true)
      try {
        await axiosInstance.post(
          `/order`,
          {
            page: values.page,
            message: values.message,
            status: 'Purchased',
            times: values.times.map((time) => time.time),
          },
        )
        formik.resetForm()
        message.success('สร้างสินค้าสำเร็จ')
      } catch (e: any) {
        const messageError = ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] || 'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
      setIsLoading(false)
    },
  })

  const handleSearchPage = async (value: string) => {
    setSearchPage(value)
    await pageList.mutate()
  }

  return (
    <>
      <Card sx={{
        padding: 2,
      }}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={6}>
            <Typography variant="h6" gutterBottom>
              ระบบขุดแชท
            </Typography>
            <Box sx={{ marginTop: '10px' }}>
              {/* <TextArea rows={4} placeholder="ใส่ข้อความ" maxLength={6} /> */}
              <TextField
                id="message"
                name="message"
                value={formik.values.message}
                onChange={formik.handleChange}
                error={formik.touched.message && Boolean(formik.errors.message)}
                helperText={formik.touched.message && formik.errors.message}
                fullWidth
                label="ใส่ข้อความ"
                multiline
                rows={4} />
            </Box>
            <Box sx={{ marginTop: '10px' }}>
              <SelectSearch<IFacebookPage>
                label={'เพจ'}
                onSelect={(e) => formik.setFieldValue('page', e?.value.pageId)}
                search={searchPage}
                onSearch={handleSearchPage}
                options={
                  pageList.data?.map((page: IFacebookPage) => ({
                    label: page.pageName,
                    value: page,
                  })) || [] as IOptionSelect<IFacebookPage>[]
                } />
            </Box>
            <Box sx={{ marginTop: '10px' }}>
              <FormControl variant="filled" fullWidth>
                <InputLabel id="status-label">สถานะ</InputLabel>
                <Select
                  id="status"
                  name="status"
                  labelId="status-label"
                  value={formik.values.status || ''}
                  onChange={(e) => formik.setFieldValue('status', e.target.value)}
                  fullWidth
                >
                  <MenuItem disabled value="" selected>
                    <em>เลือกสถานะ</em>
                  </MenuItem>
                  {menuItemsTypeStatus.map((item, index: number) => (
                    <MenuItem value={item.value} key={index}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {formik.values.times.length < 4 &&
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
                marginTop: '10px',
              }}>
                <Button
                  startIcon={<AddIcon />}
                  color="primary"
                  size="medium"
                  variant="contained"
                  onClick={() => {
                    formik.setFieldValue('times', [...formik.values.times, { time: '' }])
                  }}
                >
                  เพิ่มเวลา
                </Button>
              </Box>}
            {
              formik.values.times.map((item, index) => (
                <Box key={index}
                     sx={{
                       display: 'flex',
                       justifyContent: 'space-between',
                       width: '100%',
                       marginTop: '10px',
                     }}
                >
                  <TimePicker style={{ height: '48px', width: '100%' }} />
                  <IconButton
                    onClick={() => {
                      formik.setFieldValue('times', formik.values.times.filter((_, i) => i !== index))
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))
            }
          </Grid>
          <Grid item xs={12} lg={6}>
            <Box sx={{ marginTop: '30px' }}>
              <input
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={onHandleSelectMultiFileChange}
                id="file-input"
              />
              <label htmlFor="file-input">
                <Button
                  component="span"
                  color="inherit"
                  fullWidth
                  sx={{
                    height: '150px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: '1px solid #ddd',
                    borderStyle: 'dashed',
                    borderRadius: '8px',
                  }}
                >

                  <CloudUploadIcon />
                  <Box
                    sx={{
                      marginLeft: '10px',
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      อัพโหลดรูปภาพ 1-10 รูป
                    </Typography>
                  </Box>
                </Button>
              </label>
            </Box>
            <Box sx={{
              marginTop: '10px',
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'flex-start',
              width: '100%',
              overflow: 'auto',
            }}>
              {
                formik.values.images.map((imageUrl, index) => (
                  <Box mr={1} key={index}>
                    <ImgTag
                      size={70}
                      imageUrl={imageUrl}
                      name={imageUrl}
                      isShowCancel={true}
                      onCancel={(value) => {
                        formik.setFieldValue('images', formik.values.images.filter((_, i) => i !== index))
                      }}
                    />
                  </Box>
                ))
              }
            </Box>
            <Box
              onClick={() => formik.handleSubmit()}
              sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', marginTop: '10px' }}>
              <Button startIcon={<SaveOutlined />} variant="contained"> กดบันทึกออเดอร์ </Button>
            </Box>
          </Grid>
        </Grid>
      </Card>
    </>
  )
}

export default FacebookDiggerChat